import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../../components/layout/DashboardLayout';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import apiClient from '../../../lib/api';
import { useAuth } from '../../../context/AuthContext';

const AddUserPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(user?.companyId || '');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const loadCompanies = async () => {
    try {
      // Only need to fetch companies if the current user is an admin
      if (user?.type === 'admin') {
        const resp = await apiClient.getFuneralCompanies({ page: 1, limit: 100 });
        setCompanies(resp.data || []);
      }
    } catch (err) {
      console.error('Failed to load funeral companies', err);
    }
  };
  
  useEffect(() => {
    loadCompanies();
  }, [user]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleCancel = () => {
    navigate('/users');
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    // Require a company: either current user's companyId or a selected one
    if (user?.type === 'admin' && !selectedCompanyId) {
      newErrors.companyId = 'Please select a funeral company';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      // Automatically set type to 'staff' and associate with current user's company
      // Password will be auto-generated by the backend
      const userData = {
        ...formData,
        type: 'staff',
        companyId: user?.companyId || selectedCompanyId,
      };
      const response = await apiClient.createUser(userData);
      
      toast.success('User created successfully');
      
      // Navigate to the user's detail page
      const userId = response.user?.id || response.id || response.data?.id;
      navigate(`/users/${userId}`);

    } catch (error) {
      console.error('Error creating user:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Error creating user';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DashboardLayout title="Add New User">
      <Box sx={{ maxWidth: 800, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1">
            Add New User
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            Complete the form below to add a new user to the system. A random password will be automatically generated.
          </Typography>
          {errors.general && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {errors.general}
            </Typography>
          )}
        </Box>

        <Card>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 3 
              }}>
                {/* Company selector for admins (no companyId on current user) */}
                {user?.type === 'admin' && (
                  <FormControl
                    fullWidth
                    required
                    error={!!errors.companyId}
                    sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}
                  >
                    <InputLabel>Funeral Company</InputLabel>
                    <Select
                      value={selectedCompanyId}
                      label="Funeral Company"
                      displayEmpty
                      onChange={(e) => setSelectedCompanyId(e.target.value)}
                    >
                      {/* <MenuItem value="" disabled>
                        Select a funeral company
                      </MenuItem> */}
                      {companies.length === 0 && (
                        <MenuItem value="" disabled>
                          No funeral companies found
                        </MenuItem>
                      )}
                      {companies.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.companyName || 'Unnamed Company'}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.companyId && (
                      <FormHelperText>{errors.companyId}</FormHelperText>
                    )}
                  </FormControl>
                )}

                <TextField
                  fullWidth
                  label="First Name"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  required
                />
                <TextField
                  fullWidth
                  label="Last Name"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                  required
                />
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  error={!!errors.email}
                  helperText={errors.email}
                  required
                  sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}
                />
                <TextField
                  fullWidth
                  label="Phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="Optional phone number"
                  sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}
                />
              </Box>

              {/* Action Buttons */}
              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                justifyContent: 'flex-end',
                mt: 4,
                pt: 3,
                borderTop: '1px solid',
                borderColor: 'divider'
              }}>
                <Button
                  onClick={handleCancel}
                  startIcon={<ArrowBackIcon />}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isLoading}
                  startIcon={<CheckIcon />}
                >
                  Create User
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Box>
    </DashboardLayout>
  );
};

export default AddUserPage;

